import { useCallback, useMemo, useState } from 'react';
import { LessonType } from '@/enums/enums';
import { useClassCategoryColors } from '@/features/class-category/class-category.queries';
import { useLessonTypes } from '@/features/lesson/lesson-type.queries';
import { useCalendarDefaultFilter, useLessons } from '@/features/lesson/lesson.queries';
import { getTextColor } from '@/utils/text-utils';
import { createFileRoute } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CalendarFilter } from '@/types/lesson.types';
import { Option } from '@/types/types';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import BigCalendar from '@/components/calendar/big-calendar';
import FilterPopover from '@/components/filters/filter-popover';
import { calendarFilterSections } from '@/components/filters/filter-sections';
import ContractViewSheet from '@/components/sheets/contract-sheet/contract-view-sheet';
import LessonSheet from '@/components/sheets/lesson-sheet/lesson-sheet';
import LessonViewSheet from '@/components/sheets/lesson-sheet/lesson-view-sheet';

const homeRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view']).optional(),
  lesson: z.number().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  roomId: z.number().optional(),
  roomName: z.string().optional(),
});

export const Route = createFileRoute('/_app/')({
  validateSearch: zodSearchValidator(homeRouteSchema),
  component: HomePage,
});

type CalendarSelections = {
  rooms: Option[];
  classes: Option[];
  teachers: Option[];
};

const emptySelections: CalendarSelections = { rooms: [], classes: [], teachers: [] };

function HomePage() {
  const { t } = useTranslation(['common']);
  const { data: calendarDefaultFilter, isLoading: isLoadingFilter } = useCalendarDefaultFilter();

  const defaultFromApi = useMemo(() => {
    if (!calendarDefaultFilter) return null;
    return {
      rooms: calendarDefaultFilter.rooms ?? [],
      classes: calendarDefaultFilter.classes ?? [],
      teachers: calendarDefaultFilter.teachers ?? [],
      dateRange:
        calendarDefaultFilter.from && calendarDefaultFilter.to
          ? { from: calendarDefaultFilter.from, to: calendarDefaultFilter.to }
          : null,
    };
  }, [calendarDefaultFilter]);

  /** Non-null after the user changes filters or applies the popover — avoids syncing API defaults via an effect */
  const [selectionOverrides, setSelectionOverrides] = useState<CalendarSelections | null>(null);
  const [dateRangeOverrides, setDateRangeOverrides] = useState<{ from: string; to: string } | null>(
    null
  );

  const selections = useMemo((): CalendarSelections => {
    if (selectionOverrides !== null) return selectionOverrides;
    if (defaultFromApi) {
      return {
        rooms: defaultFromApi.rooms,
        classes: defaultFromApi.classes,
        teachers: defaultFromApi.teachers,
      };
    }
    return emptySelections;
  }, [defaultFromApi, selectionOverrides]);

  const initialSelections = useMemo((): CalendarSelections => {
    if (!defaultFromApi) return emptySelections;
    return {
      rooms: defaultFromApi.rooms,
      classes: defaultFromApi.classes,
      teachers: defaultFromApi.teachers,
    };
  }, [defaultFromApi]);

  const dateRange = useMemo(() => {
    if (dateRangeOverrides !== null) return dateRangeOverrides;
    if (defaultFromApi?.dateRange) return defaultFromApi.dateRange;
    return { from: '', to: '' };
  }, [defaultFromApi, dateRangeOverrides]);

  const { data: lessonTypes = [], isLoading: isLoadingLessonTypes } = useLessonTypes();

  const filterData: CalendarFilter = useMemo(
    () => ({ ...dateRange, ...selections }),
    [dateRange, selections]
  );

  const { data: lessons = [], isLoading: isLoadingLessons } = useLessons({
    filterData,
    enabled: !!dateRange.from && !!dateRange.to,
  });

  const onRangeChange = useCallback((date: { from: string; to: string }) => {
    setDateRangeOverrides(date);
  }, []);

  const onFiltersChange = useCallback(
    (newFilters: Record<string, unknown>) => {
      setSelectionOverrides((prev) => {
        const base =
          prev ??
          (defaultFromApi
            ? {
                rooms: defaultFromApi.rooms,
                classes: defaultFromApi.classes,
                teachers: defaultFromApi.teachers,
              }
            : emptySelections);
        return { ...base, ...newFilters } as CalendarSelections;
      });
    },
    [defaultFromApi]
  );

  const filterCounter = useMemo(
    () =>
      [selections.rooms, selections.classes, selections.teachers].filter((arr) => arr.length > 0)
        .length,
    [selections]
  );

  const resourceMap = useMemo(
    () =>
      selections.rooms?.map((room) => ({
        resourceId: room.value as number,
        resourceTitle: room.label,
      })),
    [selections.rooms]
  );

  const isLoading = isLoadingLessonTypes || isLoadingLessons || isLoadingFilter;

  return (
    <>
      <div className='flex h-full min-h-0 w-full flex-1 flex-col gap-y-2 px-4 py-2'>
        {isLoading && (
          <div className='bg-background/60 absolute inset-0 z-50 flex items-center justify-center backdrop-blur-xs'>
            <div className='bg-background flex items-center gap-2 rounded-lg px-4 py-3 shadow-lg'>
              <Loader2 className='text-primary size-5 animate-spin' />
              <span className='text-muted-foreground text-sm font-medium'>{t('loading')}...</span>
            </div>
          </div>
        )}

        <div className='flex items-center gap-3'>
          <FilterPopover
            sections={calendarFilterSections}
            counter={filterCounter}
            filters={selections}
            initialFilters={initialSelections}
            onFiltersChange={onFiltersChange}
          />
          <CalendarLegend lessonTypes={lessonTypes} />
        </div>

        <div className='min-h-0 flex-1'>
          <BigCalendar lessons={lessons} rangeChange={onRangeChange} resourceMap={resourceMap} />
        </div>
      </div>
      <LessonSheet />
      <LessonViewSheet />
      <ContractViewSheet />
    </>
  );
}

// ─── Legend (status colors + class colors) ────────────────────────────────────

function CalendarLegend({
  lessonTypes,
}: {
  lessonTypes: { id: number; name: string; color: string }[];
}) {
  const { t } = useTranslation(['common', 'lesson']);
  const [open, setOpen] = useState(false);
  const { data: classColors = [], isLoading: isLoadingClasses } = useClassCategoryColors();

  const statusBadges = [
    { label: t('lesson:status.CANCELED', 'Ακυρώθηκε'), color: 'bg-red-500', text: 'text-white' },
    { label: t('lesson:status.NORMAL', 'Κανονικό'), color: 'bg-gray-500', text: 'text-white' },
    {
      label: t('lesson:status.REPLACEMENT', 'Αναπλήρωση'),
      color: 'bg-blue-500',
      text: 'text-white',
    },
    {
      label: t('lesson:status.REPLACED', 'Αναπληρώθηκε'),
      color: 'bg-green-600',
      text: 'text-white',
    },
    {
      label: t('lesson:status.TO_BE_REPLACED', 'Προς Αναπλήρωση'),
      color: 'bg-teal-500',
      text: 'text-white',
    },
  ];

  const customTypes = lessonTypes.filter(
    (lt) => ![LessonType.Normal, LessonType.Replacement, LessonType.Replaced].includes(lt.id)
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className='flex items-center gap-3'>
        <div className='flex flex-wrap items-center gap-1.5'>
          {statusBadges.map((badge) => (
            <Badge key={badge.label} className={cn('text-xs font-medium', badge.color, badge.text)}>
              {badge.label}
            </Badge>
          ))}
          {customTypes.map((lt) => (
            <Badge
              key={lt.id}
              className='text-xs font-medium'
              style={{ backgroundColor: lt.color, color: getTextColor(lt.color) }}
            >
              {lt.name}
            </Badge>
          ))}
        </div>

        <CollapsibleTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='text-muted-foreground h-7 gap-1 px-2 text-xs'
          >
            {t('classes')}
            <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className='flex flex-wrap items-center gap-1.5 pt-2'>
          {isLoadingClasses ? (
            <Loader2 className='text-muted-foreground size-4 animate-spin' />
          ) : (
            classColors
              .filter((cls) => cls.id !== 0)
              .map((cls) => (
                <Badge
                  key={cls.id}
                  className='text-xs font-medium'
                  style={{ backgroundColor: cls.color, color: getTextColor(cls.color) }}
                >
                  {cls.name}
                </Badge>
              ))
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
